'use client';

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ChevronDown, ChevronLeft, Sparkles, CheckCircle2, Target,
  BookOpen, Activity, Award, Plus, Sigma, FlaskConical, PenLine, Globe,
  ScrollText, Users, Cpu, Palette, Briefcase, HeartPulse, Cross, Compass,
  Telescope, Crown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { SageLearnerInsight } from '@/components/education/SageLearnerInsight';

// ─── Design system — navy monochrome + one gold accent ───────────────────────
// FOUR colours: navy surfaces, white text, muted text, gold accent.
// Rose exists ONLY as a semantic exception for scores under 60%.
// Every text pair ≥ 6:1. No text below 11px. Gold is never small body text.
const C = {
  canvas:   '#081325ff',                    // page — deep navy
  heroTop:  '#16213A',                    // hero gradient fades INTO canvas
  card:     '#0F1930',                    // raised card
  tile:     '#0A0F1E',                    // tile inside a card
  well:     'rgba(255,255,255,0.045)',    // inset row inside a tile
  line:     'rgba(255,255,255,0.09)',
  lineSoft: 'rgba(255,255,255,0.055)',
  track:    'rgba(255,255,255,0.09)',     // progress-bar track
  text:     '#F2F5FA',
  muted:    '#A6AEC0',
  gold:     '#e9ae25ff',                    // accent: ring, bars, active tab
  goldSoft: '#da9c18ff',                    // gold as small text on dark (7.6:1)
  goldDeep: '#f1b829ff',
  goldTint: 'rgba(201,168,92,0.13)',
  goldLine: 'rgba(201,168,92,0.32)',
  ink:      '#141B2E',                    // navy text on gold/white surfaces
  white:    '#FFFFFF',
  rose:     '#e82a53ff',                    // ONLY scores < 60
  roseTint: 'rgba(238,158,176,0.13)',
} as const;

const serif = "'Playfair Display', Georgia, serif";
const sans  = "'Inter', -apple-system, 'SF Pro Display', system-ui, sans-serif";
const ease  = [0.22, 1, 0.36, 1] as [number, number, number, number];

const micro: CSSProperties     = { fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' };
const caption: CSSProperties   = { fontSize: 12, color: C.muted };
const cardStyle: CSSProperties = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 18 };

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,500&display=swap');
  @keyframes cp-spin { to { transform: rotate(360deg); } }
  .cp-root { color-scheme: dark; -webkit-font-smoothing: antialiased; }
  .cp-root button { font-family: inherit; }
  .cp-root button:focus-visible { outline: 2px solid #D4B36A; outline-offset: 2px; }
  .cp-row { transition: background-color 0.15s ease; }
  .cp-row:hover { background-color: rgba(255,255,255,0.04); }
  .cp-ghost { transition: color 0.15s ease; }
  .cp-ghost:hover { color: #fff; }
  .cp-dashed { transition: border-color 0.15s ease, color 0.15s ease; }
  .cp-dashed:hover { border-color: rgba(201,168,92,0.55) !important; color: #F2F5FA !important; }
  .cp-event:last-child { border-bottom: none !important; }
  @media (prefers-reduced-motion: reduce) {
    .cp-root *, .cp-root *::before, .cp-root *::after {
      animation-duration: 0.01ms !important; transition-duration: 0.01ms !important;
    }
  }
`;

const DEMO_ID = '00000000-0000-4000-a000-000000000a4a';

// ─── Types (unchanged) ────────────────────────────────────────────────────────
interface SubjectProgress {
  class_id: string; class_name: string; subject_group: string; category_id: string;
  mastery_score: number; last_active_at: string; joined_at: string;
  progress_state: { current_unit: number; current_lesson: number; total_lessons_completed: number } | null;
  achievements: string[]; module_plan: any[] | null;
  lesson_count?: number | null;
  essay_mode?: string | null;
}
interface ChildDetail {
  id: string; name: string; age: number; age_bracket: string;
  subjects: SubjectProgress[]; learnerProfile?: any; global_academic_profile?: any;
}
interface LessonLog {
  id: string; unit_number: number; lesson_number: number; completed_at: string; class_id: string;
  metadata: {
    class_name?: string; score?: number; phase?: string; attempt?: number; passed?: boolean;
    weak_areas?: string[]; strong_areas?: string[];
    concept_understood?: string; weak_concept_flagged?: string;
  } | null;
}
interface QuizResult {
  id: string; lesson_unit: number; lesson_number: number;
  score_percentage: number; attempted_at: string; class_id: string; questions: any;
}
type Tab = 'overview' | 'subjects' | 'activity';

// ─── Homework Reflections Components ─────────────────────────────────────────
// Returns SEQUENTIAL module positions (1-based) — class_enrollments.progress_state
// current_unit is sequential and completed_lessons uses "1-1" keys (both receipted).
function completedModuleIds(course: { module_plan?: any[] | null; progress_state?: any }): number[] {
  const done: Record<string, boolean> = course.progress_state?.completed_lessons || {}
  const currUnit = course.progress_state?.current_unit ?? 1
  const completed: number[] = []
  if (Array.isArray(course.module_plan)) {
    course.module_plan.forEach((m, i) => {
      const pos = i + 1
      if (pos < currUnit) { completed.push(pos); return }
      const lessons = Array.isArray(m.lessons) ? m.lessons : []
      if (lessons.length === 0) return
      const allDone = lessons.every((l: any, li: number) =>
        !!done[`${pos}-${li + 1}`]                    // sequential (scoreQuiz's format)
        || !!done[`${m.module_id}-${l.lesson_id}`]   // hierarchical paired
        || !!done[`u${m.module_id}_l${l.lesson_id}`]  // prefixed
        || !!done[String(l.lesson_id)]                // bare
      )
      if (allDone) completed.push(pos)
    })
  }
  return completed
}

function ReflectionRow({ s }: { s: any }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const scorePct = s.ai_score != null && s.max_score ? Math.round((s.ai_score / s.max_score) * 100) : s.ai_score;
  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}`, padding: '10px 0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', width: '100%', justifyContent: 'space-between',
          alignItems: 'center', background: 'none', border: 'none',
          cursor: 'pointer', fontSize: 13, color: C.text, textAlign: 'left',
          padding: '4px 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            Module {s.module_id}
          </span>
          {scorePct != null ? (
            <span style={{
              fontSize: 11, fontWeight: 800,
              padding: '2px 8px', borderRadius: 20,
              background: scorePct >= 80 ? 'rgba(74, 222, 128, 0.15)' : C.goldTint,
              color: scorePct >= 80 ? '#4ade80' : C.goldSoft,
            }}>
              {scorePct}% ({s.ai_score}/{s.max_score})
            </span>
          ) : (
            <span style={{ fontSize: 11, color: C.muted, textTransform: 'capitalize' }}>
              {s.grading_status || 'Submitted'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 12 }}>
          <span>{s.created_at ? new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
          <span>{open ? '▲' : '▼'}</span>
        </div>
      </button>
      {open && (
        <div style={{ paddingTop: 10, paddingBottom: 6, fontSize: 12.5, lineHeight: 1.6, color: C.text }}>
          <div style={{ background: C.well, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
            <div style={{ ...micro, color: C.muted, marginBottom: 4 }}>{t('homeschool.student_reflection')}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{s.response_text}</div>
          </div>
          {s.ai_feedback && (
            <div style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ ...micro, color: C.goldSoft, marginBottom: 4 }}>{t('homeschool.ai_grader_feedback')}</div>
              <div style={{ whiteSpace: 'pre-wrap', color: C.muted }}>{s.ai_feedback}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReflectionsSection({ childId, course }: { childId: string; course: any }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<any[] | null>(null);

  useEffect(() => {
    if (!open || rows !== null) return;
    supabase.from('university_submissions')
      .select('id, module_id, ai_score, max_score, grading_status, created_at, response_text, ai_feedback')
      .eq('user_id', childId)
      .eq('class_id', course.class_id)
      .order('created_at', { ascending: false })
      .then(({ data }) => setRows(data ?? []));
  }, [open, rows, childId, course.class_id]);

  if (!course.essay_mode || course.essay_mode === 'none') return null;

  const pending = rows ? completedModuleIds(course)
    .filter(pos => !rows.some(r => Number(r.module_id) === pos
      || Number(r.module_id) === Number(course.module_plan?.[pos - 1]?.module_id))) : [];

  return (
    <div style={{ marginTop: 14, marginBottom: 10, background: C.tile, border: `1px solid ${C.lineSoft}`, borderRadius: 12, padding: '12px 14px' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', width: '100%', justifyContent: 'space-between',
          alignItems: 'center', background: 'none', border: 'none',
          cursor: 'pointer', color: C.text, textAlign: 'left', padding: 0
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>
            ✍️ Written Reflections{rows ? ` (${rows.length}${pending.length ? ` · ${pending.length} pending` : ''})` : ''}
          </span>
          {pending.length > 0 && (
            <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 12, background: C.goldTint, color: C.goldSoft, border: `1px solid ${C.goldLine}` }}>
              {pending.length} pending
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: C.muted }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {pending.map(m => (
            <div key={m} style={{ fontSize: 12, color: C.goldSoft, padding: '6px 0', borderBottom: `1px solid ${C.lineSoft}` }}>
              📝 Module {m}, reflection pending
            </div>
          ))}
          {rows?.map((s, idx) => (
            <ReflectionRow key={s.id || `${s.module_id}-${idx}`} s={s} />
          ))}
          {rows?.length === 0 && !pending.length && (
            <div style={{ fontSize: 12, color: C.muted, padding: '6px 0', fontStyle: 'italic' }}>
              No reflections written yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// Monochrome status: white numbers always; rose ONLY under 60.
const masteryTone = (s: number) =>
  s >= 60
    ? { fg: C.text, bg: 'rgba(255,255,255,0.07)' }
    : { fg: C.rose, bg: C.roseTint };

const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtS = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';
const fmtDay = (d: string) => {
  if (!d) return '—';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const ageBracketLabel = (t: any, b: string) => {
  if (b === 'primary') return t('homeschool.child.ageBracket.primary');
  if (b === 'secondary') return t('homeschool.child.ageBracket.secondary');
  if (b === 'sixth_form' || b === 'sixth-form') return t('homeschool.child.ageBracket.sixth_form');
  return b;
};

const GROUP_ICONS: Record<string, LucideIcon> = {
  'Mathematics': Sigma, 'Sciences': FlaskConical, 'English & Writing': PenLine,
  'Languages': Globe, 'Humanities': ScrollText, 'Social Sciences': Users,
  'Technology & Computing': Cpu, 'Arts & Performance': Palette,
  'Business & Finance': Briefcase, 'Life Skills & Wellbeing': HeartPulse,
  'Christian Studies': Cross, 'Careers & Study Skills': Compass,
  'Astronomy & Space': Telescope, 'Elite Academy': Crown, 'General': BookOpen,
};
const GROUP_ORDER = [
  'Mathematics', 'Sciences', 'English & Writing', 'Languages', 'Humanities',
  'Social Sciences', 'Technology & Computing', 'Arts & Performance',
  'Business & Finance', 'Life Skills & Wellbeing', 'Christian Studies',
  'Careers & Study Skills', 'Astronomy & Space', 'Elite Academy', 'General',
];

// ─── Small components ─────────────────────────────────────────────────────────

function CountUp({ value, instant }: { value: number; instant?: boolean }) {
  const [n, setN] = useState(instant ? value : 0);
  useEffect(() => {
    if (instant) { setN(value); return; }
    let raf: number | null = null;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / 900);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [value, instant]);
  return <>{n}</>;
}

function MasteryRing({ score, label, size = 132, reduce }: {
  score: number; label: string; size?: number; reduce: boolean;
}) {
  const stroke = 10;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const finalOffset = circ - (score / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,0.14)" strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={C.gold} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: reduce ? finalOffset : circ }}
          animate={{ strokeDashoffset: finalOffset }}
          transition={{ duration: reduce ? 0 : 1.1, ease, delay: 0.2 }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: size * 0.26, fontWeight: 800, color: C.white, fontFamily: sans, letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          <CountUp value={score} instant={reduce} />
          <span style={{ fontSize: size * 0.13, color: C.muted, fontWeight: 700 }}> %</span>
        </div>
        <div style={{ ...micro, letterSpacing: '0.12em', color: C.muted, marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );
}

function StatBlock({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div style={{ ...micro, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: C.white, marginTop: 3, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function Chips({ items, tight }: { items: { label: string; fg: string; bg: string; border?: string }[]; tight?: boolean }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((x, i) => (
        <span key={i} style={{
          fontSize: 12, fontWeight: 600, color: x.fg, background: x.bg,
          border: `1px solid ${x.border || 'transparent'}`,
          padding: tight ? '3px 9px' : '4px 10px', borderRadius: 8,
        }}>{x.label}</span>
      ))}
    </div>
  );
}

function EventRow({ log, defaultOpen }: { log: LessonLog; defaultOpen?: boolean }) {
  const { t } = useTranslation();
  const reduce = !!useReducedMotion();
  const [open, setOpen] = useState(!!defaultOpen);
  const m = log.metadata || {};
  const isQuiz = m.score !== undefined;
  const tone = masteryTone(m.score || 0);
  const Icon = isQuiz ? ((m.score ?? 0) >= 60 ? CheckCircle2 : Target) : BookOpen;

  return (
    <div className="cp-event" style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      <button onClick={() => setOpen(!open)} className="cp-row"
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 11, flexShrink: 0,
          background: isQuiz ? tone.bg : C.well,
          color: isQuiz ? tone.fg : C.muted,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={17} strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t('homeschool.child.activityLabel', { className: m.class_name || 'Lesson', unit: log.unit_number, lesson: log.lesson_number })}
          </div>
          <div style={{ ...caption, marginTop: 2 }}>
            {fmtDay(log.completed_at)}
            {isQuiz ? ` · ${t('homeschool.child.activityType.quiz', { score: m.score })}` : ` · ${t('homeschool.child.activityType.teaching')}`}
          </div>
        </div>
        {isQuiz && (
          <span style={{ fontSize: 16, fontWeight: 800, color: tone.fg, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{m.score}%</span>
        )}
        <ChevronDown size={15} color={C.muted} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.24, ease }} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '2px 18px 18px 68px', display: 'flex', flexDirection: 'column', gap: 10 }}>

              {m.strong_areas && m.strong_areas.length > 0 && (
                <div>
                  <div style={{ ...micro, color: C.muted, marginBottom: 6 }}>{t('homeschool.child.insights.strong')}</div>
                  <Chips items={m.strong_areas.map(a => ({ label: a, fg: C.goldSoft, bg: C.goldTint, border: C.goldLine }))} />
                </div>
              )}
              {m.weak_areas && m.weak_areas.length > 0 && (
                <div>
                  <div style={{ ...micro, color: C.muted, marginBottom: 6 }}>{t('homeschool.child.insights.focus')}</div>
                  <Chips items={m.weak_areas.map(a => ({ label: a, fg: C.muted, bg: C.well, border: C.lineSoft }))} />
                </div>
              )}

              {(m.concept_understood || m.weak_concept_flagged) && (
                <div style={{ background: C.well, borderRadius: 12, padding: '12px 14px' }}>
                  <div style={{ ...micro, color: C.muted, marginBottom: 6 }}>{t('homeschool.child.insights.aiTeacherTitle')}</div>
                  {m.concept_understood && (
                    <p style={{ margin: 0, fontFamily: serif, fontStyle: 'italic', fontSize: 14, lineHeight: 1.55, color: C.text }}>“{m.concept_understood}”</p>
                  )}
                  {m.weak_concept_flagged && (
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: C.text, marginTop: m.concept_understood ? 8 : 0 }}>
                      <span style={{ fontWeight: 700, color: C.muted }}>{t('homeschool.child.insights.struggled')}</span> {m.weak_concept_flagged}
                    </p>
                  )}
                </div>
              )}

              {(!m.weak_areas && !m.concept_understood) && (
                <p style={{ ...caption, fontStyle: 'italic', margin: 0 }}>{t('homeschool.child.noMetrics')}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ChildProgressPage() {
  const { childId } = useParams<{ childId: string }>();
  const { user, userProfile, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const reduce = !!useReducedMotion();

  const [child, setChild] = useState<ChildDetail | null>(null);
  const [recentLogs, setRecentLogs] = useState<LessonLog[]>([]);
  const [quizResults, setQuizResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);
  const [expandedModule, setExpandedModule] = useState<number | null>(null);
  const [showParentFeedback, setShowParentFeedback] = useState<Record<string, boolean>>({});
  const [parentFeedbackDraft, setParentFeedbackDraft] = useState<Record<string, string>>({}); 

  const isDemo = childId === DEMO_ID || childId === 'demo-ava';

  useEffect(() => {
    if (!authLoading && !user) { navigate('/signin', { replace: true }); return; }
    if (childId) loadChildDetail();
  }, [childId, userProfile?.facility_id, authLoading]);

  async function loadChildDetail() {
    setLoading(true);
    try {
      if (!isDemo && userProfile?.facility_role !== 'admin') { navigate('/homeschool/parent'); return; }

      if (isDemo) {
        const { data: show } = await supabase.from('demo_child_showcase').select('*').maybeSingle();
        if (!show) return;

        const rawEnrollments = (Array.isArray(show.enrollments) ? show.enrollments : []) as Array<Record<string, any>>;
        const enrol = rawEnrollments[0];
        const ps = (enrol?.progress_state as any) ?? { current_unit: 2, current_lesson: 3, total_lessons_completed: 5 };

        // FIX: real class → real module_plan & subject_group (no Mathematics hardcode)
        const { data: cls } = await supabase
          .from('classes').select('module_plan, subject_group')
          .eq('id', enrol?.class_id).maybeSingle();

        const age = show.child_age ?? 12;
        setChild({
          id: DEMO_ID,
          name: show.child_name || 'Ava',
          age,
          age_bracket: age >= 16 ? 'sixth_form' : age >= 12 ? 'secondary' : 'primary',
          learnerProfile: show.global_academic_profile || null,   // FIX: feeds the insight card
          subjects: rawEnrollments.map((e: any) => ({
            class_id: e.class_id || '',
            class_name: e.class_name || 'Sample Course',
            subject_group: (cls as any)?.subject_group || e.subject_group || 'Elite Academy',
            mastery_score: Number(e.mastery_score) || 0,
            category_id: (e.classes as any)?.category_id || '',
            last_active_at: e.last_active_at || '',
            joined_at: e.joined_at || e.last_active_at || '',
            progress_state: e.progress_state || ps,
            achievements: e.achievements || [],
            module_plan: (cls?.module_plan as any[]) || [],
          })),
        });

        const rawLogs = (Array.isArray(show.recent_logs) ? show.recent_logs : []) as Array<Record<string, any>>;
        setRecentLogs(rawLogs.map((l: any, idx: number) => ({
          id: `demo-log-${idx}`,
          unit_number: l.unit_number, lesson_number: l.lesson_number,
          completed_at: l.completed_at, class_id: enrol?.class_id || '',
          metadata: l.metadata || {},
        })));

        const quizLogs = rawLogs.filter((l: any) => l.metadata?.phase === 'quiz_result' || l.metadata?.score !== undefined);
        setQuizResults(quizLogs.map((q: any, idx: number) => ({
          id: `demo-quiz-${idx}`,
          lesson_unit: q.unit_number, lesson_number: q.lesson_number,
          score_percentage: q.metadata?.score ?? 80,
          attempted_at: q.completed_at, class_id: enrol?.class_id || '',
          questions: [],
        })));
        return;
      }

      if (!userProfile?.facility_id) return;
      const { data: childUser } = await supabase
        .from('users').select('id, name, age, age_bracket, global_academic_profile')
        .eq('id', childId!).eq('facility_id', userProfile.facility_id!)
        .eq('facility_role', 'member').single();
      if (!childUser) { navigate('/homeschool/parent'); return; }

      const [enrolRes, progRes, bcastRes] = await Promise.all([
        supabase
          .from('class_enrollments')
          .select('class_id, classes(name, subject_group, module_plan, essay_mode, lesson_count, category_id)')
          .eq('student_id', childId!),
        // Real stats live here — the class_enrollments stat columns are never
        // written by anyone. Broadcast fan-out materializes progress rows only.
        supabase
          .from('student_subject_progress')
          .select('class_id, mastery_score, current_unit, current_lesson, lessons_completed, achievements, last_activity_at')
          .eq('user_id', childId!),
        // Broadcast courses carry no enrollment rows — union them in or the
        // parent never sees them.
        supabase
          .from('classes')
          .select('id, name, subject_group, module_plan, essay_mode, lesson_count, category_id')
          .eq('facility_id', userProfile.facility_id!)
          .eq('enrollment_mode', 'all')
          .eq('is_published', true),
      ]);
      const enrollments = enrolRes.data || [];
      const progByClass = new Map<string, any>(
        ((progRes.data || []) as any[]).map(p => [p.class_id, p])
      );
      const enrolledClassIds = new Set(enrollments.map((e: any) => e.class_id));
      const broadcast = ((bcastRes.data || []) as any[]).filter(c => !enrolledClassIds.has(c.id));

      const { data: logs } = await supabase
        .from('student_lesson_logs')
        .select('id, unit_number, lesson_number, completed_at, metadata, class_id')
        .eq('user_id', childId!).order('completed_at', { ascending: false }).limit(100);

      const { data: quizzes } = await supabase
        .from('education_quiz_results')
        .select('id, lesson_unit, lesson_number, score_percentage, attempted_at, class_id, questions')
        .eq('user_id', childId!).order('attempted_at', { ascending: false }).limit(100);

      const discovered: any[] = [
        ...enrollments.map((e: any) => ({ ...(e.classes || {}), id: e.class_id })),
        ...broadcast,
      ];
      const subjects: SubjectProgress[] = discovered.map(c => {
        const prog: any = progByClass.get(c.id);
        const doneTags: string[] = Array.isArray(prog?.lessons_completed) ? prog.lessons_completed : [];
        return {
          class_id: c.id,
          class_name: c.name || 'Unknown',
          subject_group: c.subject_group || 'General',
          category_id: c.category_id || '',
          mastery_score: Math.round(Number(prog?.mastery_score) || 0),
          last_active_at: prog?.last_activity_at || '',
          joined_at: prog?.last_activity_at || '',
          progress_state: {
            current_unit: prog?.current_unit ?? 1,
            current_lesson: prog?.current_lesson ?? 1,
            total_lessons_completed: doneTags.length,
            completed_lessons: Object.fromEntries(doneTags.map(k => [k, true])),
          } as any,
          achievements: (prog?.achievements as string[]) || [],
          module_plan: (c.module_plan as any[]) || null,
          lesson_count: c.lesson_count ? Number(c.lesson_count) : null,
          essay_mode: (c.essay_mode as string) || null,
        };
      });

      const childAge = childUser.age || 0;
      const derivedBracket = (childUser.age_bracket && ['primary', 'secondary', 'sixth_form', 'sixth-form', 'post16'].includes(childUser.age_bracket))
        ? childUser.age_bracket
        : (childAge >= 16 ? 'sixth_form' : childAge >= 12 ? 'secondary' : 'primary');

      setChild({
        id: childUser.id, name: childUser.name || 'Child', age: childAge,
        age_bracket: derivedBracket, subjects,
        learnerProfile: (childUser as any).global_academic_profile || null,
      });
      setRecentLogs((logs || []) as any[]);
      setQuizResults((quizzes || []) as any[]);

      const groups = new Set(subjects.map(s => s.subject_group));
      const sorted = GROUP_ORDER.filter(g => groups.has(g));
      setExpandedGroups(new Set(sorted.slice(0, 3)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const groupedSubjects = useMemo(() => {
    if (!child) return [];
    const map = new Map<string, SubjectProgress[]>();
    for (const s of child.subjects) {
      const arr = map.get(s.subject_group) || [];
      arr.push(s); map.set(s.subject_group, arr);
    }
    return GROUP_ORDER.filter(g => map.has(g)).map(g => ({ group: g, items: map.get(g)! }))
      .concat(Array.from(map.entries()).filter(([g]) => !GROUP_ORDER.includes(g)).map(([group, items]) => ({ group, items })));
  }, [child]);

  const getLogsForModule = (classId: string, unitNumber: number) =>
    recentLogs.filter(l => l.class_id === classId && l.unit_number === unitNumber);
  const getQuizzesForModule = (classId: string, unitNumber: number) =>
    quizResults.filter(q => q.class_id === classId && q.lesson_unit === unitNumber);

  if (authLoading || loading) {
    return (
      <div className="cp-root" style={{ minHeight: '100dvh', background: C.canvas, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, fontFamily: sans }}>
        <style>{GLOBAL_CSS}</style>
        <div style={{ width: 28, height: 28, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.12)', borderTopColor: C.gold, animation: 'cp-spin 0.9s linear infinite' }} />
        <span style={{ color: C.muted, fontSize: 14 }}>{t('homeschool.child.loading')}</span>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="cp-root" style={{ minHeight: '100dvh', background: C.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: sans }}>
        <style>{GLOBAL_CSS}</style>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: C.muted, fontSize: 14, margin: '0 0 14px' }}>{t('homeschool.this_report_isn_available_right_now')}</p>
          <button onClick={() => navigate('/homeschool/parent')} className="cp-row"
            style={{ background: 'none', border: 'none', color: C.text, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: '6px 12px', borderRadius: 8 }}>
            {t('homeschool.child.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = child.subjects.reduce((s, e) => s + (e.progress_state?.total_lessons_completed || 0), 0);
  const avgMastery = child.subjects.length
    ? Math.round(child.subjects.reduce((s, e) => s + e.mastery_score, 0) / child.subjects.length) : 0;

  const TABS: { key: Tab; label: string }[] = [
    { key: 'overview', label: t('homeschool.child.overview') },
    { key: 'subjects', label: t('homeschool.child.subjects') },
    { key: 'activity', label: t('homeschool.child.activity') },
  ];

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cp-root"
      style={{ minHeight: '100dvh', background: C.canvas, fontFamily: sans }}>
      <style>{GLOBAL_CSS}</style>

      {/* ══ HERO — fades into the page canvas; no seam, no "different bottom" ══ */}
      <div style={{ background: `linear-gradient(180deg, ${C.heroTop} 0%, ${C.canvas} 100%)`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -140, right: -90, width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(201,168,92,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '18px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button onClick={() => navigate('/homeschool/parent')} className="cp-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: C.muted }}>
            <ChevronLeft size={15} /> {t('homeschool.child.backToDashboard')}
          </button>
          <button onClick={() => navigate(`/homeschool/child/${childId}/subjects`)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)', color: C.text, fontSize: 12.5, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer' }}>
            <Plus size={13} /> {t('homeschool.child.addSubject')}
          </button>
        </div>

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '22px 24px 44px', display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
          <MasteryRing score={avgMastery} label={t('homeschool.child.stats.mastery')} reduce={reduce} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 7, flexWrap: 'wrap' }}>
              <h1 style={{ fontFamily: serif, fontSize: 34, fontWeight: 800, color: C.white, margin: 0, letterSpacing: '-0.01em', lineHeight: 1.1 }}>{child.name}</h1>
              {isDemo && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...micro, letterSpacing: '0.12em', color: C.goldSoft, background: C.goldTint, border: `1px solid ${C.goldLine}`, padding: '5px 11px', borderRadius: 999 }}>
                  <Sparkles size={11} /> Sample
                </span>
              )}
            </div>
            <p style={{ color: C.muted, fontSize: 15, margin: '0 0 18px' }}>
              {t('homeschool.child.ageDescription', { age: child.age, bracket: ageBracketLabel(t, child.age_bracket) })}
            </p>
            <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap' }}>
              <StatBlock label={t('homeschool.child.stats.subjects')} value={child.subjects.length} />
              <StatBlock label={t('homeschool.child.stats.lessons')} value={totalLessons} />
            </div>
          </div>
        </div>
      </div>

      {/* ══ TABS — SIBLING of the hero, never inside its overflow:hidden ══
          (this was the clipping bug: negative margin + parent overflow:hidden
          sliced the pills and stranded white text on the white page below) */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', marginTop: -26, position: 'relative', zIndex: 6 }}>
        <div role="tablist" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.line}`, borderRadius: 14, padding: 4, display: 'flex', gap: 4 }}>
          {TABS.map(x => (
            <button key={x.key} role="tab" aria-selected={activeTab === x.key} onClick={() => setActiveTab(x.key)}
              style={{
                flex: '1 1 0', minWidth: 0, padding: '10px 8px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: activeTab === x.key ? `linear-gradient(135deg, ${C.goldSoft}, ${C.gold})` : 'transparent',
                color: activeTab === x.key ? C.ink : C.muted,
                fontSize: 13, fontWeight: 700, fontFamily: sans,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'all 0.2s',
              }}>
              {x.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ BODY — same material as the hero ══ */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '34px 24px 96px' }}>
        <AnimatePresence mode="wait">

          {/* ── OVERVIEW ── */}
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: reduce ? 0 : 0.25, ease }}>

              {/* The insight renders as a white "report" on the dark desk —
                  the one light surface, ruled in gold. Also guarantees the
                  third-party component's light-theme internals stay readable. */}
              {child.learnerProfile?.summary && (
                <div style={{ marginBottom: 20, background: C.white, borderRadius: 18, overflow: 'hidden', boxShadow: '0 10px 34px rgba(0,0,0,0.38)' }}>
                  <div style={{ height: 3, background: `linear-gradient(90deg, ${C.goldSoft}, ${C.goldDeep})` }} />
                  <SageLearnerInsight
                    profile={child.learnerProfile}
                    learnerName={child.name}
                    accentColor="#8A6B24"
                    cardBackground="#FFFFFF"
                  />
                </div>
              )}

              {/* Latest quiz — TODO: extract strings to i18n */}
              {(() => {
                const lastQuiz = recentLogs.find(l => l.metadata?.score !== undefined);
                if (!lastQuiz) return null;
                const tone = masteryTone(lastQuiz.metadata?.score || 0);
                return (
                  <div style={{ ...cardStyle, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Activity size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...micro, color: C.muted, marginBottom: 4 }}>{t('homeschool.latest_quiz')}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                        Lesson {lastQuiz.unit_number}.{lastQuiz.lesson_number}, <span style={{ color: tone.fg, fontWeight: 800 }}>{lastQuiz.metadata?.score}%</span>
                        {(lastQuiz.metadata?.attempt ?? 1) > 1 && <span style={{ color: C.muted, fontWeight: 600, fontSize: 13 }}> · passed on retry</span>}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Subject mastery — white numbers, gold bars */}
              <div style={{ ...cardStyle, padding: '6px 20px', marginBottom: 20 }}>
                <div style={{ padding: '16px 0 12px', fontSize: 17, fontWeight: 800, color: C.text }}>{t('homeschool.child.subjectMastery')}</div>
                {child.subjects.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '34px 0 40px' }}>
                    <BookOpen size={28} color={C.muted} style={{ margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ ...caption, margin: '0 0 12px' }}>{t('homeschool.child.noSubjectsYet')}</p>
                    <button onClick={() => navigate(`/homeschool/child/${childId}/subjects`)}
                      style={{ background: 'none', border: 'none', color: C.text, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                      {t('homeschool.child.addSubjectsLink')}
                    </button>
                  </div>
                ) : child.subjects.map((sub, idx) => {
                  const tone = masteryTone(sub.mastery_score);
                  const planTotal = sub.lesson_count || (sub.module_plan || []).reduce((s: number, m: any) => s + (m.lessons?.length || 0), 0)
                  const pct = planTotal > 0 ? Math.min(Math.round((sub.progress_state?.total_lessons_completed || 0) / planTotal * 100), 100) : 0;
                  return (
                    <div key={sub.class_id} style={{ padding: '14px 0', borderTop: idx === 0 ? 'none' : `1px solid ${C.lineSoft}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14.5, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.class_name}</div>
                          <div style={{ ...caption, marginTop: 2 }}>
                            {t('homeschool.child.currentUnitLesson', { unit: sub.progress_state?.current_unit, lesson: sub.progress_state?.current_lesson })}
                            {' · '}{t('homeschool.child.totalLessons', { count: sub.progress_state?.total_lessons_completed || 0 })}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: tone.fg, fontVariantNumeric: 'tabular-nums' }}>{sub.mastery_score || 0}%</div>
                          <div style={{ ...caption, marginTop: 2 }}>{fmtS(sub.last_active_at)}</div>
                        </div>
                      </div>
                      <div style={{ height: 5, background: C.track, borderRadius: 999, marginTop: 10, overflow: 'hidden' }}>
                        <motion.div initial={{ width: reduce ? `${pct}%` : 0 }} animate={{ width: `${pct}%` }}
                          transition={{ duration: reduce ? 0 : 0.8, delay: reduce ? 0 : idx * 0.05, ease }}
                          style={{ height: '100%', borderRadius: 999, background: C.gold }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Achievements — the gold layer */}
              <div style={{ ...cardStyle, padding: '18px 20px' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 14 }}>{t('homeschool.child.achievements')}</div>
                {child.subjects.flatMap(s => s.achievements).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '10px 0 14px' }}>
                    <Award size={26} color={C.muted} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.7 }} />
                    <p style={{ ...caption, margin: 0 }}>{t('homeschool.child.achievementsHint', { name: child.name })}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {child.subjects.flatMap(s => s.achievements).map((a, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: C.goldSoft, background: C.goldTint, border: `1px solid ${C.goldLine}`, padding: '6px 12px', borderRadius: 999 }}>
                        <Award size={12} /> {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Demo conversion */}
              {isDemo && (
                <div style={{ marginTop: 24, background: C.card, border: `1px solid ${C.goldLine}`, borderRadius: 18, padding: '24px 26px', textAlign: 'center' }}>
                  <div style={{ ...micro, letterSpacing: '0.14em', color: C.goldSoft, marginBottom: 8 }}>{t('homeschool.this_is_sample')}</div>
                  <p style={{ fontFamily: serif, fontSize: 19, fontWeight: 600, color: C.text, margin: '0 0 16px', lineHeight: 1.45 }}>
                    Every child gets this same depth, real lessons, real quizzes, real insight into how they learn.
                  </p>
                  <button onClick={() => navigate('/homeschool/enrol?mode=child')}
                    style={{ padding: '13px 28px', borderRadius: 13, border: 'none', background: `linear-gradient(135deg, ${C.goldSoft}, ${C.gold})`, color: C.ink, fontSize: 14.5, fontWeight: 800, cursor: 'pointer', fontFamily: sans, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
                    Start Your Child's Journey
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {/* ── SUBJECTS ── */}
          {activeTab === 'subjects' && (
            <motion.div key="subjects" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: reduce ? 0 : 0.25, ease }}>

              {child.subjects.length === 0 ? (
                <div style={{ ...cardStyle, padding: '48px 24px', textAlign: 'center' }}>
                  <BookOpen size={30} color={C.muted} style={{ margin: '0 auto 12px', display: 'block' }} />
                  <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 6 }}>{t('homeschool.child.noSubjects')}</div>
                  <p style={{ ...caption, margin: '0 0 18px' }}>{t('homeschool.child.browseHint', { name: child.name })}</p>
                  <button onClick={() => navigate(`/homeschool/child/${childId}/subjects`)}
                    style={{ padding: '12px 24px', borderRadius: 12, border: 'none', background: C.white, color: C.ink, fontWeight: 800, fontSize: 13.5, cursor: 'pointer' }}>
                    {t('homeschool.child.browseSubjects')}
                  </button>
                </div>
              ) : (
                <>
                  {groupedSubjects.map(({ group, items }) => {
                    const isGroupExpanded = expandedGroups.has(group);
                    const GroupIcon = GROUP_ICONS[group] || BookOpen;
                    return (
                      <div key={group} style={{ ...cardStyle, marginBottom: 14, overflow: 'hidden' }}>
                        <button onClick={() => toggleGroup(group)} className="cp-row"
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                          <div style={{ width: 36, height: 36, borderRadius: 11, background: C.tile, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <GroupIcon size={17} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t(`homeschool.groups.${group}`, group)}</div>
                            <div style={{ ...caption, marginTop: 2 }}>
                              {t('homeschool.child.groupSummary', { count: items.length, avg: Math.round(items.reduce((s, i) => s + i.mastery_score, 0) / items.length) })}
                            </div>
                          </div>
                          <ChevronDown size={16} color={C.muted} style={{ transform: isGroupExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                        </button>

                        <AnimatePresence initial={false}>
                          {isGroupExpanded && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: reduce ? 0 : 0.25, ease }} style={{ overflow: 'hidden' }}>
                              <div style={{ padding: '2px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>

                                {items.map(sub => {
                                  const isSubExpanded = expandedSubject === sub.class_id;
                                  const tone = masteryTone(sub.mastery_score);
                                  return (
                                    <div key={sub.class_id} style={{ background: C.tile, border: `1px solid ${isSubExpanded ? C.goldLine : C.lineSoft}`, borderRadius: 14, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                                      <button onClick={() => setExpandedSubject(isSubExpanded ? null : sub.class_id)} className="cp-row"
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.class_name}</div>
                                          <div style={{ ...caption, marginTop: 2 }}>
                                            {t('homeschool.child.currentUnitLesson', { unit: sub.progress_state?.current_unit, lesson: sub.progress_state?.current_lesson })}
                                            {' · '}{t('homeschool.child.totalLessons', { count: sub.progress_state?.total_lessons_completed || 0 })}
                                          </div>
                                        </div>
                                        <span style={{ fontSize: 15.5, fontWeight: 800, color: tone.fg, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{sub.mastery_score || 0}%</span>
                                        <ChevronDown size={15} color={C.muted} style={{ transform: isSubExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                      </button>

                                      <AnimatePresence initial={false}>
                                        {isSubExpanded && (
                                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: reduce ? 0 : 0.25, ease }} style={{ overflow: 'hidden' }}>
                                            <div style={{ padding: '4px 16px 16px', borderTop: `1px solid ${C.lineSoft}`, display: 'flex', flexDirection: 'column', gap: 8 }}>

                                              <div style={{ height: 5, background: C.track, borderRadius: 999, overflow: 'hidden', marginBottom: 4 }}>
                                              <div style={{ height: '100%', borderRadius: 999, background: C.gold, width: `${(() => { const pt = sub.lesson_count || (sub.module_plan || []).reduce((s: number, m: any) => s + (m.lessons?.length || 0), 0); return pt > 0 ? Math.min(Math.round((sub.progress_state?.total_lessons_completed || 0) / pt * 100), 100) : 0 })()}%` }} />
                                              </div>

                                              {sub.module_plan && sub.module_plan.length > 0 ? sub.module_plan.map((mod: any, modIdx: number) => {
                                                const unitNum = modIdx + 1;
                                                const isModExpanded = expandedModule === modIdx;
                                                const moduleLogs = getLogsForModule(sub.class_id, unitNum);
                                                const moduleQuizzes = getQuizzesForModule(sub.class_id, unitNum);
                                                const lessons = Array.isArray(mod.lessons) ? mod.lessons : [];
                                                const avgQuiz = moduleQuizzes.length > 0
                                                  ? Math.round(moduleQuizzes.reduce((s, q) => s + q.score_percentage, 0) / moduleQuizzes.length)
                                                  : null;
                                                return (
                                                  <div key={modIdx} style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 12, overflow: 'hidden' }}>
                                                    <button onClick={() => setExpandedModule(isModExpanded ? null : modIdx)} className="cp-row"
                                                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                                      <span style={{ width: 24, height: 24, borderRadius: 8, background: C.goldTint, color: C.goldSoft, fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        {unitNum}
                                                      </span>
                                                      <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                          {mod.title || mod.name || `Module ${unitNum}`}
                                                        </div>
                                                        <div style={{ ...caption, marginTop: 1 }}>
                                                          {t('homeschool.child.moduleStats', { total: lessons.length, done: moduleLogs.length, tests: moduleQuizzes.length })}
                                                        </div>
                                                      </div>
                                                      {avgQuiz !== null && (
                                                        <span style={{ fontSize: 13, fontWeight: 800, color: masteryTone(avgQuiz).fg, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{avgQuiz}%</span>
                                                      )}
                                                      <ChevronDown size={13} color={C.muted} style={{ transform: isModExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                                                    </button>

                                                    <AnimatePresence initial={false}>
                                                      {isModExpanded && (
                                                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                                                          transition={{ duration: reduce ? 0 : 0.22, ease }} style={{ overflow: 'hidden' }}>
                                                          <div style={{ padding: '2px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>

                                                            {lessons.map((lesson: any, li: number) => {
                                                              const lessonNum = li + 1;
                                                              const lessonTitle = typeof lesson === 'string' ? lesson : (lesson.title || lesson.name || `Lesson ${lessonNum}`);
                                                              const lessonLog = moduleLogs.find(l => l.lesson_number === lessonNum);
                                                              const lessonQuiz = moduleQuizzes.find(q => q.lesson_number === lessonNum);
                                                              const lm = lessonLog?.metadata;
                                                              const hasInsight = lm && (lm.concept_understood || lm.weak_concept_flagged || lm.strong_areas || lm.weak_areas);
                                                              return (
                                                                <div key={li} style={{ background: C.well, borderRadius: 10, padding: '10px 12px' }}>
                                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                                                    <div style={{
                                                                      width: 19, height: 19, borderRadius: '50%', flexShrink: 0,
                                                                      border: `1.5px solid ${lessonLog ? C.gold : C.line}`,
                                                                      background: lessonLog ? C.gold : 'transparent',
                                                                      color: C.ink, fontSize: 10, fontWeight: 800,
                                                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}>
                                                                      {lessonLog ? '✓' : lessonNum}
                                                                    </div>
                                                                    <span style={{ fontSize: 13, color: lessonLog ? C.text : C.muted, fontWeight: lessonLog ? 600 : 500, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                      {lessonTitle}
                                                                    </span>
                                                                    {lessonQuiz && (
                                                                      <span style={{ fontSize: 12.5, fontWeight: 800, color: masteryTone(lessonQuiz.score_percentage).fg, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                                                        {t('homeschool.child.quizScore', { score: lessonQuiz.score_percentage })}
                                                                      </span>
                                                                    )}
                                                                    {lessonLog && <span style={{ ...caption, flexShrink: 0 }}>{fmtS(lessonLog.completed_at)}</span>}
                                                                  </div>

                                                                  {hasInsight && (
                                                                    <div style={{ marginLeft: 28, marginTop: 9, display: 'flex', flexDirection: 'column', gap: 7 }}>
                                                                      {lm!.strong_areas && lm!.strong_areas!.length > 0 && (
                                                                        <div>
                                                                          <div style={{ ...micro, color: C.muted, marginBottom: 5 }}>{t('homeschool.child.insights.strong')}</div>
                                                                          <Chips tight items={lm!.strong_areas!.map(a => ({ label: a, fg: C.goldSoft, bg: C.goldTint, border: C.goldLine }))} />
                                                                        </div>
                                                                      )}
                                                                      {lm!.weak_areas && lm!.weak_areas!.length > 0 && (
                                                                        <div>
                                                                          <div style={{ ...micro, color: C.muted, marginBottom: 5 }}>{t('homeschool.child.insights.focus')}</div>
                                                                          <Chips tight items={lm!.weak_areas!.map(a => ({ label: a, fg: C.muted, bg: 'rgba(255,255,255,0.05)', border: C.lineSoft }))} />
                                                                        </div>
                                                                      )}
                                                                      {(lm!.concept_understood || lm!.weak_concept_flagged) && (
                                                                        <div style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 10, padding: '10px 12px' }}>
                                                                          <div style={{ ...micro, color: C.muted, marginBottom: 5 }}>{t('homeschool.child.insights.aiTitle')}</div>
                                                                          {lm!.concept_understood && (
                                                                            <p style={{ margin: 0, fontFamily: serif, fontStyle: 'italic', fontSize: 13.5, lineHeight: 1.55, color: C.text }}>“{lm!.concept_understood}”</p>
                                                                          )}
                                                                          {lm!.weak_concept_flagged && (
                                                                            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.55, color: C.text, marginTop: lm!.concept_understood ? 7 : 0 }}>
                                                                              <span style={{ fontWeight: 700, color: C.muted }}>{t('homeschool.child.insights.struggled')}</span> {lm!.weak_concept_flagged}
                                                                            </p>
                                                                          )}
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  )}

                                                                  {!lessonLog && (
                                                                    <div style={{ marginLeft: 28, marginTop: 6, ...caption, fontStyle: 'italic' }}>{t('homeschool.child.notStarted')}</div>
                                                                  )}
                                                                </div>
                                                              );
                                                            })}

                                                            {lessons.length === 0 && (
                                                              <p style={{ ...caption, fontStyle: 'italic', margin: 0, padding: '4px 2px' }}>{t('homeschool.child.aiGenerated')}</p>
                                                            )}
                                                          </div>
                                                        </motion.div>
                                                      )}
                                                    </AnimatePresence>
                                                  </div>
                                                );
                                              }) : (
                                                <div style={{ background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 12, padding: '12px 14px' }}>
                                                  <p style={{ ...caption, fontStyle: 'italic', margin: 0 }}>{t('homeschool.child.aiGeneratedDetailed')}</p>
                                                </div>
                                              )}

                                              <ReflectionsSection childId={childId!} course={sub} />

                                              {/* ── Parent encouragement (homeschool & family_edu only) ── */}
                                              {!isDemo && userProfile?.facility_type !== 'school' && (
                                                <>
                                                  <button
                                                    onClick={() => setShowParentFeedback(v => ({ ...v, [sub.class_id]: !v[sub.class_id] }))}
                                                    style={{ width: '100%', marginTop: 4, padding: '10px 0', borderRadius: 12, border: `1px solid ${C.line}`, background: C.well, color: C.text, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                                    💛 Leave Encouragement for {child?.name?.split(' ')[0] || 'them'}
                                                  </button>
                                                  {showParentFeedback[sub.class_id] && (
                                                    <div style={{ marginTop: 6, background: C.card, borderRadius: 12, padding: 12 }}>
                                                      <textarea
                                                        value={parentFeedbackDraft[sub.class_id] || ''}
                                                        onChange={e => setParentFeedbackDraft(p => ({ ...p, [sub.class_id]: e.target.value }))}
                                                        placeholder={`An encouraging note or improvement point, visible in their lessons and printed on their record.`}
                                                        rows={3}
                                                        style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${C.line}`, background: 'transparent', color: C.text, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: sans }}
                                                      />
                                                      <button
                                                        onClick={async () => {
                                                          const body = parentFeedbackDraft[sub.class_id]?.trim();
                                                          if (!body) return;
                                                          try {
                                                            const { error } = await supabase.from('teacher_feedback' as any).insert({
                                                              student_id: childId!,
                                                              class_id: sub.class_id,
                                                              author_id: user!.id,
                                                              feedback_type: 'encouragement',
                                                              targets_lesson: false,
                                                              body,
                                                              visible_to_student: true,
                                                            });
                                                            if (error) throw error;
                                                            setParentFeedbackDraft(p => ({ ...p, [sub.class_id]: '' }));
                                                            setShowParentFeedback(p => ({ ...p, [sub.class_id]: false }));
                                                          } catch (err) {
                                                            console.error('Encouragement insert failed:', err); // draft preserved — user can retry
                                                          }
                                                        }}
                                                        style={{ marginTop: 8, width: '100%', padding: 10, borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${C.goldSoft}, ${C.gold})`, color: C.ink, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                                                        Send Encouragement
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              )}

                                              <button onClick={() => navigate(`/learn/session?class=${sub.class_id}&category=${sub.category_id ?? ''}`)}
                                                style={{ width: '100%', marginTop: 4, padding: '12px 0', borderRadius: 12, border: 'none', background: C.white, color: C.ink, fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>
                                                {t('homeschool.child.continueLearning')}
                                              </button>
                                            </div>
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}

                  <button onClick={() => navigate(`/homeschool/child/${childId}/subjects`)} className="cp-dashed"
                    style={{ width: '100%', border: `1.5px dashed ${C.line}`, background: 'transparent', borderRadius: 16, padding: '15px 0', color: C.muted, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                    {t('homeschool.child.addAnotherSubject')}
                  </button>
                </>
              )}
            </motion.div>
          )}

          {/* ── ACTIVITY ── */}
          {activeTab === 'activity' && (
            <motion.div key="activity" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: reduce ? 0 : 0.25, ease }}>
              <div style={{ ...cardStyle, overflow: 'hidden' }}>
                <div style={{ padding: '16px 18px 10px', fontSize: 17, fontWeight: 800, color: C.text }}>{t('homeschool.child.recentActivity')}</div>
                {recentLogs.length === 0 ? (
                  <div style={{ padding: '44px 24px', textAlign: 'center' }}>
                    <BookOpen size={28} color={C.muted} style={{ margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ ...caption, margin: 0 }}>{t('homeschool.child.noActivity', { name: child.name })}</p>
                  </div>
                ) : recentLogs.slice(0, 25).map((log, i) => (
                  <EventRow key={log.id} log={log} defaultOpen={i === 0} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}